import { useTranslation } from "react-i18next";

import { API_BASE_URL } from "@/lib/api";

/**
 * LoginButton — anchors the user to `${API_BASE_URL}/auth/login`.
 *
 * This MUST be a full-page navigation (<a href>, not fetch) because the
 * WorkOS AuthKit flow requires a top-level redirect: WorkOS replies with
 * a 302 to its hosted UI, then back to `/auth/callback`, and finally to
 * whatever `redirect` we passed. None of that works inside an XHR.
 */
export interface LoginButtonProps {
  /**
   * Path to return to after sign-in. Defaults to the current pathname so
   * "click Sign in on /study/foo → land back on /study/foo" just works. The
   * backend enforces an allowlist (see `REDIRECT_ALLOWLIST` in
   * `backend/.../AuthRoutes.kt`) so unknown paths fall back to `/`.
   */
  returnTo?: string;
  className?: string;
}

export default function LoginButton({ returnTo, className }: LoginButtonProps) {
  const { t } = useTranslation();
  const target =
    typeof window !== "undefined" ? (returnTo ?? window.location.pathname) : (returnTo ?? "/");
  const href = `${API_BASE_URL}/auth/login?redirect=${encodeURIComponent(target)}`;

  const classes =
    className ??
    "inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary";

  return (
    <a href={href} className={classes} data-testid="login-button">
      {t("auth.signIn")}
    </a>
  );
}
